// @ts-strict-ignore
/**
 * Created by ppatton on 2/28/2017.
 */
import { Injectable } from '@angular/core';
import { FormRequestType } from '@utils/app.constants';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMFormRequest } from '@form/lm-form.model';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, first } from 'rxjs/operators';
import { LMGenAIModelResponse } from '@modal/lm-genai-prompt-dialog';
import { LMNavigationService } from '@components/navigation/lm-navigation.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMGlobalUtilService } from '@utils/lm-global-util.service';
import urltemplate from "url-template";
import {LMAdHocResponse, LMAdHocSelectedField} from "@app/components";

@Injectable()
export class LMFormRequestService {
  constructor(
    private http: HttpClient,
    private lmLoggerService: LMLoggerService,
    private lmNavigationService: LMNavigationService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalUtilService: LMGlobalUtilService
  ) {}

  // "requestType":"userDefinedStates",
  // "fieldId":"PrmSource",
  // "dataView":{  },
  // "action":"SubmitJobApplicationInProgress",

  // pageSize = -1 means infinite page size, so all records are returned
  public getUserDefinedStates(
    formUrl: string,
    fieldId: string,
    dataView: LMDataView,
    actionName?: string,
    pageSize = -1,
    selectListInfo: LMSelectListInfo = null,
    adhocFields: LMAdHocSelectedField[] = null
  ): Observable<LMUserDefinedResponse> {
    const formRequest: LMFormRequest = {
      requestType: FormRequestType.UserDefinedStates,
      fieldId,
      dataView,
      action: actionName,
      pageSize
    };

    formRequest.selectListName = '';
    if (selectListInfo) {
      formRequest.selectListName = selectListInfo.selectListName;

      if (selectListInfo.selectLink) {
      formRequest.selectLink = selectListInfo.selectLink;
      }
    }

    if (adhocFields && adhocFields.length > 0) {
      formRequest.adhocFields = adhocFields;
    }

    // const formRequest: LMFormRequest = Object.assign(
    //   { requestType: FormRequestType.UserDefinedStates },
    //   { fieldId, dataView, action: actionName, pageSize, selectListName, selectLink });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public fireInitialValueRules(formUrl: string, fieldId: string, dataView: LMDataView, actionName?: string, sourceResource?: Resource): Observable<LMFields> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.InitialValue },
      { fieldId, dataView, action: actionName });

    if (sourceResource) {
      formRequest.sourceResourceType = sourceResource.resourceType;
      formRequest.sourceResourceId = sourceResource.resourceId;
    }

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getDefineFormUrl(formUrl: string, fieldId: string, dataView: LMDataView, actionName?: string): Observable<DefineFormSpecUrlResponse> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.DefineFormUrl },
      { fieldId, dataView, action: actionName });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getFieldsForBusinessMetric(formUrl: string, businessMetric: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.BusinessMetricFields },
      { businessMetric: businessMetric });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getConditionNamesForBusClass(formUrl: string, busClassName: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.ConditionNamesForBusClass },
      { param1: busClassName });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getFieldValues(formUrl: string, fieldList: Array<string>): Observable<LMFieldValuesResponse> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.FieldValues },
      { param1: fieldList });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getAdHocSearchForm(formUrl: string, fieldList: Array<LMAdHocSelectedField>): Observable<LMAdHocResponse> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.AdHocSearchForm },
      { param1: fieldList });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getImplicitSearchFormForAdHoc(formUrl: string): Observable<LMAdHocResponse> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.ImplicitSearchFormForAdHoc });

    return this.executeFormRequest(formUrl, formRequest);
  }

  //TODO: do we still use this?
  public getBusinessClassOntology(formUrl: string, businessClass: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      {
        requestType: FormRequestType.BusClassOntology,
        businessClass });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getRelatedBusClasses(formUrl: string, businessClass: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign({
      requestType: FormRequestType.RelatedBusClassNames,
      businessClass
    });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getBusClasses(formUrl: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.BusClassNames });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getSecurableActionsList(formUrl: string, secObjectName: string, secObjectType: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      {
        requestType: FormRequestType.SecurableActionsList,
        param1: secObjectName,
        param2: secObjectType }
    );

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getSecurableObjectsList(formUrl: string, secObjectType: string, containment: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      {
        requestType: FormRequestType.SecurableObjectsList,
        param1: secObjectType,
        param2: containment }
    );

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getListNamesAndViewsForBusClass(formUrl: string, businessClass: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.ListNamesAndViewsForBusClass },
      { businessClass });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getCubeDimensions(formUrl: string, busClassName: string, listName: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.CubeDimensions },
      { param1: busClassName },
      { param2: listName });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getDisplayFields(formUrl: string, busClassName: string, listName: string) {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.ListDisplayFields },
      { busClassName },
      { listName });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getModules(formUrl: string, busClassName: string, listName: string) {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.Modules },
      { busClassName },
      { listName });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getCategoriesModule(formUrl: string, busClassName: string, listName: string) {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.CategoriesByModule },
      { busClassName },
      { listName });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getWebApps(formUrl: string, busClassName: string, listName: string) {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.WebApps },
      { busClassName },
      { listName });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getListDetails(formUrl: string, requestType: FormRequestType, busClassName: string, criteria?: any) {
    const formRequest: LMFormRequest = Object.assign({
      requestType,
      busClassName,
      criteria
    });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getPrimaryList(formUrl: string, requestType: FormRequestType, busClassName: string) {
    const formRequest: LMFormRequest = Object.assign({
      requestType,
      busClassName
    });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public validateFields(formUrl: string, busClassName: string, listName: string, displayFields: any[]): Observable<any> {
    const formRequest: any = {
      requestType: FormRequestType.DisplayFieldsSpec,
      busClassName,
      listName,
      displayFields
    };

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getListViewsForBusClass(formUrl: string, busClassName: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.ListNamesForBusClass },
      { param1: busClassName });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public refreshSubPanelViews(formUrl: string, dataViews: LMDataView[]): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.RefreshSubPanelList },
      { param1: dataViews });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getDataViewFieldButtonFormInfo(formUrl: string, fieldId: string, dataView: LMDataView, displayedSelectDialog = false): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.DataViewDataFormInfo },
      { fieldId },
      { dataView },
      { param1: displayedSelectDialog });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getDataViewFieldButtonValue(formUrl: string, dataView: LMDataView, fieldId: string, fieldDataView: LMDataView, busClassName: string, busFormName: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.DataViewDataFieldValue },
      { dataView },
      { fieldId },
      { param1: fieldDataView },
      { param2: busClassName },
      { param3: busFormName });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public validateLplQuery(formUrl: string, dataView: LMDataView, lpl: string, busClass: string,
    isBusinessClassCompute = false, computeType = ''): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.ValidateCondition },
      { param1: busClass },
      { param2: lpl },
      { dataView },
      { isBusinessClassCompute },
      { computeType });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public validateCubeRelationLplQuery(formUrl: string, lpl: string, busClass: string): Observable<any> {
    const formRequest: LMFormRequest = Object.assign(
      { requestType: FormRequestType.CubeRelValidateLPL },
      { param1: busClass },
      { param2: lpl });

    return this.executeFormRequest(formUrl, formRequest);
  }

  public getSearchInitialValues(url: string): Observable<any> {
    const request: LMFormRequest = {
      requestType: FormRequestType.SearchInitialValues,
    };
    return this.executeFormRequest(url, request);
  }

  public getSearchDrillbackValues(url: string, filterCriteria: LMFilterFieldCriteria[]): Observable<any> {
    const request: LMFormRequest = Object.assign(
      { requestType: FormRequestType.SearchDrillbackValues },
      { param1: filterCriteria });
    return this.executeFormRequest(url, request);
  }

  public runHttpUrl(httpUrl: LMMessageRule, openExternal: boolean, isLazy: boolean, variables: LMRelatedValueSpec[], 
    fieldId: string, dataView: LMDataView, requestUrl: string, resourceId: string, postProcessUrl: boolean = true) {
    let url: string;
 
    if (!httpUrl) {
      // doing this for safety. This function will handle httpUrls only, 
      // linkNavigation should be handled differently.
      return;
    }

    if (isLazy && variables) {
      this.handleLazyUrl(requestUrl, resourceId, httpUrl, openExternal, postProcessUrl);
      return;
    } else if (httpUrl && httpUrl?.variables && !httpUrl.hasContext) {
      url = this.lmFormatUtilService.formatMessageRule(httpUrl, dataView, undefined, false);
    } else if (dataView?.buttonLinks?.[fieldId]) {
      url = dataView.buttonLinks[fieldId];
    } else {
      url = this.lmFormatUtilService.formatMessageRule(httpUrl, dataView, undefined, false);
    }
    if (url.indexOf('web10x=true') > -1) {
      // Add csk parameters to the URL built by WebUrlBuilder for processflow
      url = this.lmUrlUtilService.propagateSessionKeys(url, '&');
    }
    // TODO LMWEB-19598 Set context in the url for archive or asofdate?
    this.lmNavigationService.postProcessHttpUrl(url, openExternal);
  }

  public getBlankFormView(url: string, actionIndex: number): Observable<any> {
    const request: LMFormRequest = Object.assign(
        { requestType: FormRequestType.BlankFormView },
        { actionIndex: actionIndex });
    return this.executeFormRequest(url, request);
  }

  public handleLazyUrl(urlToParse: string, resourceId: string, httpUrl: LMMessageRule, openExternal: boolean, postProcessUrl = true) {
    const reqFields = Array.from(httpUrl?.variables || [], field => field?.fieldId );
    const openActionRedirectUrlTemplate = urltemplate.parse(urlToParse);
    const requestUrl = openActionRedirectUrlTemplate.expand({
      resourceId: resourceId,
    });
    
    return this.getFieldValues(requestUrl, reqFields).pipe(
      first(),
      catchError( error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Internal Server Error');
      })
    ).subscribe( (response: LMFieldValuesResponse) => {
      if (response.status === 'Completed') {
        const fields: LMDataView = { fields: response.fields };
        let url = this.lmFormatUtilService.formatMessageRule( httpUrl, fields );
        if (url.indexOf('web10x=true') > -1) {
          // Add csk parameters to the URL built by WebUrlBuilder for processflow
          url = this.lmUrlUtilService.propagateSessionKeys(url, '&');
        }

        postProcessUrl ? 
          this.lmNavigationService.postProcessHttpUrl(url, openExternal) :
          this.lmGlobalUtilService.navigate(url, null, openExternal ? 'external' : '');
          
      } else if (response.status === 'Exception') {
        this.lmErrorUtilService.handleError(response?.message, response?.details, response.status);
      }
    });
  }

  public getGenAILLMModels(
    formUrl: string
  ): Observable<LMGenAIModelResponse> {
    const formRequest: LMFormRequest = {
      requestType: FormRequestType.GenAILLMModels,
    };

    return this.executeFormRequest(formUrl, formRequest);
  }

  private executeFormRequest(url: string, formRequest: LMFormRequest): Observable<any> {
    return this.http
      .post(url, formRequest).pipe(
        catchError(error => {
          this.lmLoggerService.error(error);
          return throwError(error || 'Server error');
        }));
  }
}

export interface DefineFormSpecUrlResponse {
  defineFormResourceId: string;
  defineFormUrl: string;
  status: LMServerResponseStatus;
  statusCode?: number;
  statusText?: string;
  details?: string;
  message?: string;
}
